import type { MutationFunctionContext } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import {
  CreateParams,
  Identifier,
  RaRecord,
  UpdateParams,
  useDataProvider,
  useGetManyReference,
  useNotify,
  useRedirect,
  useRefresh,
  useResourceContext,
  useResourceDefinition,
  useTranslate,
} from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';
import type { BatchOperation, GPDataProvider } from '../../DataProvider';
import { LabelResource } from '../../Entities/Label';
import { ProductResource } from '../../Entities/Product';
import { ProductLabelResource } from '../../Entities/ProductLabel';
import {
  getCharacteristicKey,
  type LabelFieldValue,
  toFormValue,
  toStoredValue,
} from './LabelsTab/utils';

type SaveSuccessArgs = [
  RaRecord<Identifier>,
  CreateParams<RaRecord<Identifier>> | UpdateParams<RaRecord<Identifier>>,
  unknown,
  MutationFunctionContext,
];

export type ProductSaveSuccessHandler = (...args: SaveSuccessArgs) => Promise<void>;

function getDefaultCreateRedirect(hasShow: boolean, hasEdit: boolean): string {
  if (hasEdit) {
    return 'edit';
  }

  if (hasShow) {
    return 'show';
  }

  return 'list';
}

export default function useProductSaveWithLabels(
  mode: 'create' | 'edit',
): ProductSaveSuccessHandler {
  const { getValues } = useFormContext();
  const dataProvider = useDataProvider<GPDataProvider>();
  const notify = useNotify();
  const redirect = useRedirect();
  const refresh = useRefresh();
  const resource = useResourceContext();
  const { hasEdit = false, hasShow = false } = useResourceDefinition({ resource });
  const translate = useTranslate();
  const productId = useWatch({ name: 'id' }) as string | undefined;
  const categoryId = useWatch({ name: 'relationships.category.data.id' }) as string | undefined;

  const { data: labels = [] } = useGetManyReference<LabelResource>(
    'labels',
    {
      id: categoryId ?? '',
      target: 'category.id',
      pagination: { page: 1, perPage: 250 },
      sort: { field: 'attributes.display_order', order: 'ASC' },
    },
    {
      enabled: Boolean(categoryId),
    },
  );

  const { data: productLabels = [], refetch: refetchProductLabels } =
    useGetManyReference<ProductLabelResource>(
      'product_labels',
      {
        id: productId ?? '',
        target: 'product.id',
        pagination: { page: 1, perPage: 250 },
        sort: { field: 'attributes.value', order: 'ASC' },
      },
      {
        enabled: Boolean(productId),
      },
    );

  const existingByLabelId = useMemo(
    () =>
      new Map(
        productLabels
          .map((productLabel) => {
            const labelId = productLabel.id.split(':', 2)[1];
            return labelId ? [labelId, productLabel] : null;
          })
          .filter((entry): entry is [string, ProductLabelResource] => entry !== null),
      ),
    [productLabels],
  );

  const syncProductLabels = useCallback(
    async (savedProductId: string): Promise<void> => {
      if (!categoryId) {
        return;
      }

      const values = getValues('characteristics') as Record<string, LabelFieldValue> | undefined;
      const operations: BatchOperation[] = [];

      for (const label of labels) {
        const storedValue = toStoredValue(
          values?.[getCharacteristicKey(label.id)] ?? toFormValue(label, undefined),
        );
        const existing = existingByLabelId.get(label.id);

        if (storedValue.length === 0) {
          if (existing) {
            operations.push({
              op: 'remove',
              ref: {
                id: existing.id,
                type: 'product_labels',
              },
            });
          }

          continue;
        }

        if (existing) {
          if (existing.attributes.value !== storedValue) {
            operations.push({
              op: 'update',
              data: {
                id: existing.id,
                type: 'product_labels',
                attributes: {
                  ...existing.attributes,
                  value: storedValue,
                },
              },
            });
          }

          continue;
        }

        operations.push({
          op: 'add',
          data: {
            type: 'product_labels',
            attributes: {
              value: storedValue,
            },
            relationships: {
              label: {
                data: {
                  id: label.id,
                  type: 'labels',
                },
              },
              product: {
                data: {
                  id: savedProductId,
                  type: 'products',
                },
              },
            },
          },
        });
      }

      if (operations.length > 0) {
        await dataProvider.batch(operations);
      }

      refresh();
      await refetchProductLabels();
    },
    [categoryId, dataProvider, existingByLabelId, getValues, labels, refetchProductLabels, refresh],
  );

  return useCallback(
    async (...args: SaveSuccessArgs): Promise<void> => {
      const savedProduct = args[0] as ProductResource;
      await syncProductLabels(String(savedProduct.id));

      if (mode === 'edit') {
        notify(`resources.${resource}.notifications.updated`, {
          type: 'info',
          messageArgs: {
            smart_count: 1,
            _: translate('ra.notification.updated', {
              smart_count: 1,
            }),
          },
        });

        redirect('list', resource, savedProduct.id, savedProduct);
        return;
      }

      notify(`resources.${resource}.notifications.created`, {
        type: 'info',
        messageArgs: {
          smart_count: 1,
          _: translate('ra.notification.created', {
            smart_count: 1,
          }),
        },
      });

      redirect(getDefaultCreateRedirect(hasShow, hasEdit), resource, savedProduct.id, savedProduct);
    },
    [hasEdit, hasShow, mode, notify, redirect, resource, syncProductLabels, translate],
  );
}
